import { apiSlice } from "../../app/api/apiSlice";

export const logsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        // Get all activity logs (Admin/Supervisor only)
        getActivityLogs: builder.query({
            query: (params = {}) => {
                const queryParams = new URLSearchParams();
                
                // Add all non-empty parameters to query string
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        queryParams.append(key, value);
                    }
                });

                return {
                    url: `/logs?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                // Handle the nested response structure
                if (response && response.success && response.data) {
                    return response.data;
                }
                return response;
            },
            transformErrorResponse: (response, meta, arg) => {
                // Better error handling for different response types
                let message = 'Failed to load logs';
                
                if (response.status === 403) {
                    message = 'Access denied. You do not have permission to view system logs.';
                } else if (response.status === 401) {
                    message = 'Authentication required. Please log in again.';
                } else if (response.data?.message) {
                    message = response.data.message;
                } else if (typeof response.data === 'string') {
                    message = response.data;
                } else if (response.error) {
                    message = response.error;
                }
                
                return {
                    status: response.status,
                    message: message
                };
            },
            providesTags: ['ActivityLogs'],
            keepUnusedDataFor: 30, // Keep data for 30 seconds
        }),

        // Get filtered logs with advanced filtering
        getFilteredLogs: builder.query({
            query: ({ page, limit, filters }) => {
                const queryParams = new URLSearchParams();
                
                // Add pagination parameters
                if (page) queryParams.append('page', page);
                if (limit) queryParams.append('limit', limit);
                
                // Add filter parameters
                if (filters) {
                    Object.entries(filters).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== '') {
                            // Handle array values (join with comma)
                            if (Array.isArray(value) && value.length > 0) {
                                queryParams.append(key, value.join(','));
                            } else if (!Array.isArray(value)) {
                                queryParams.append(key, value);
                            }
                        }
                    });
                }

                return {
                    url: `/logs/filtered?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                let message = 'Failed to load filtered logs';
                
                if (response.status === 403) {
                    message = 'Access denied. You do not have permission to view system logs.';
                } else if (response.status === 401) {
                    message = 'Authentication required. Please log in again.';
                } else if (response.data?.message) {
                    message = response.data.message;
                } else if (typeof response.data === 'string') {
                    message = response.data;
                } else if (response.error) {
                    message = response.error;
                }
                
                return {
                    status: response.status,
                    message: message
                };
            },
            providesTags: ['ActivityLogs'],
            keepUnusedDataFor: 30,
        }),

        // Get user's own activity logs
        getMyActivityLogs: builder.query({
            query: (params = {}) => {
                const queryParams = new URLSearchParams();
                
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        queryParams.append(key, value);
                    }
                });

                return {
                    url: `/logs/my-activity?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                // Handle the nested response structure
                if (response && response.success && response.data) {
                    return response.data;
                }
                return response;
            },
            transformErrorResponse: (response, meta, arg) => {
                // Better error handling for different response types
                let message = 'Failed to load your activity logs';
                
                if (response.status === 403) {
                    message = 'Access denied. You do not have permission to view activity logs.';
                } else if (response.status === 401) {
                    message = 'Authentication required. Please log in again.';
                } else if (response.data?.message) {
                    message = response.data.message;
                } else if (typeof response.data === 'string') {
                    message = response.data;
                } else if (response.error) {
                    message = response.error;
                }
                
                return {
                    status: response.status,
                    message: message
                };
            },
            providesTags: ['MyActivityLogs'],
            keepUnusedDataFor: 30,
        }),

        // Get activity summary/statistics
        getActivitySummary: builder.query({
            query: (params = {}) => {
                const queryParams = new URLSearchParams();
                
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        queryParams.append(key, value);
                    }
                });

                return {
                    url: `/logs/summary?${queryParams.toString()}`,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                // Handle the nested response structure
                if (response && response.success && response.data) {
                    return response.data;
                }
                return response;
            },
            transformErrorResponse: (response, meta, arg) => {
                // Better error handling for different response types
                let message = 'Failed to load activity summary';
                
                if (response.status === 403) {
                    message = 'Access denied. You do not have permission to view activity summary.';
                } else if (response.status === 401) {
                    message = 'Authentication required. Please log in again.';
                } else if (response.data?.message) {
                    message = response.data.message;
                } else if (typeof response.data === 'string') {
                    message = response.data;
                } else if (response.error) {
                    message = response.error;
                }
                
                return {
                    status: response.status,
                    message: message
                };
            },
            providesTags: ['ActivitySummary'],
            keepUnusedDataFor: 60, // Keep summary data longer
        }),
    })
});

export const {
    useGetActivityLogsQuery,
    useGetFilteredLogsQuery,
    useGetMyActivityLogsQuery,
    useGetActivitySummaryQuery,
    useLazyGetActivityLogsQuery,
    useLazyGetFilteredLogsQuery,
    useLazyGetMyActivityLogsQuery,
    useLazyGetActivitySummaryQuery
} = logsApiSlice;
